import React from 'react';

import { Box, Typography } from '@mui/material';
import NavNeededCountBadge from '@sharedCR/common/NavNeededCountBadge';
import PopInfo from '@sharedCR/common/PopInfo';
import { ACTION_CELL_WIDTH_WITH_BADGES } from '@sharedCR/common/SimpleTable/SimpleTable.constants';
import StatusIcon from '@sharedCR/common/StatusIcon';
import { ENTITY_REFERENCE_TYPE } from 'app/shared/components/TeamChat/TeamChat.entityReferences.utils';
import TeamChatReferenceBadge from 'app/shared/components/TeamChat/TeamChatReferenceBadge';

import { assetsMap } from 'constants/options';
import { PendingChangesActionType } from 'constants/pendingChanges';
import { PopperPlacementType } from 'constants/popover';
import { StatusEnum } from 'constants/status';
import { HORIZONTAL_ALIGNMENT } from 'constants/ui';
import { formatNumberToCurrencyString } from 'utils/number';

import { FINANCIAL_INFO_VIEWS_PATHS } from '../../FinancialInfo.constants';
import ActionButton from '../ActionButton';
import BorrowersNames from '../BorrowersNames';
import {
  actionCellStyles,
  getWrappedTextStyles,
  headCellLabelStyles,
  sharedTypographyStyles,
} from '../FinancialInfoBody.styles';

const FINICITY_TITLE = 'Finicity Verified';

export const getConfig = ({ isExpanded, onRemove, chatTargets = null, neededCountById = {} }) => ({
  ownersInfo: {
    isHidden: !isExpanded,
    label: 'Borrower',
    width: '15.5%',
    colSpan: ({ ownersInfo }) => ownersInfo?.colSpan,
    customStyles: ({ ownersInfo }) => ownersInfo?.customStyles,
    content: ({
      ownersInfo,
      modifiedFields: rowModifiedFields,
      pendingChangesActionType: rowPendingChangesActionType,
    }) =>
      ownersInfo?.content ?? (
        <BorrowersNames
          ownersInfo={ownersInfo}
          rowModifiedFields={rowModifiedFields}
          rowPendingChangesActionType={rowPendingChangesActionType}
        />
      ),
    fieldName: 'ownersFullNames',
  },
  institution: {
    label: 'Institution',
    width: isExpanded ? '15%' : '30%',
    colSpan: ({ institution }) => institution?.colSpan,
    customStyles: ({ institution }) => institution?.customStyles,
    content: ({ institution, isVerified }) =>
      institution?.content ?? (
        <div className="flex gap-8 w-full">
          <Typography
            data-truncated
            className="truncate "
            sx={sharedTypographyStyles}
            variant="regular14"
          >
            {institution ?? '-'}
          </Typography>
          {!isExpanded && isVerified && (
            <PopInfo
              arrow
              placement={PopperPlacementType.TOP_START}
              title={FINICITY_TITLE}
              variant={StatusEnum.INFO}
            >
              <span>
                <StatusIcon status={StatusEnum.SUCCESS} />
              </span>
            </PopInfo>
          )}
        </div>
      ),
  },
  assetsTypeName: {
    fieldName: 'assetTypeName',
    label: (
      <Typography data-truncated sx={headCellLabelStyles} variant="regular14">
        Assets Type
      </Typography>
    ),
    width: isExpanded ? '12%' : '20%',
    content: ({ assetTypeName }) => (
      <Typography data-truncated sx={getWrappedTextStyles()} variant="regular14">
        {assetsMap[assetTypeName]?.name ?? '-'}
      </Typography>
    ),
  },
  accountNumber: {
    label: (
      <Typography data-truncated sx={headCellLabelStyles} variant="regular14">
        Account Number
      </Typography>
    ),
    width: isExpanded ? '15%' : '20%',
    align: HORIZONTAL_ALIGNMENT.RIGHT,
    content: ({ accountNumber }) => (
      <Typography
        data-truncated
        className="truncate"
        sx={sharedTypographyStyles}
        variant="regular14"
      >
        {accountNumber ?? '-'}
      </Typography>
    ),
  },
  balance: {
    label: 'Balance',
    width: isExpanded ? '12%' : '20%',
    align: HORIZONTAL_ALIGNMENT.RIGHT,
    content: ({ balance }) => (
      <Typography
        data-truncated
        className="truncate"
        sx={sharedTypographyStyles}
        variant="regular14"
      >
        {balance ? formatNumberToCurrencyString(balance) : '-'}
      </Typography>
    ),
  },
  isVerified: {
    isHidden: !isExpanded,
    label: (
      <Typography data-truncated sx={headCellLabelStyles} variant="regular14">
        {FINICITY_TITLE}
      </Typography>
    ),
    width: '12%',
    align: HORIZONTAL_ALIGNMENT.CENTER,
    content: ({ isVerified }) => (!isVerified ? null : <StatusIcon status={StatusEnum.SUCCESS} />),
  },
  action: {
    label: 'Action',
    width: ACTION_CELL_WIDTH_WITH_BADGES,
    content: ({ id, pendingChangesActionType }) => {
      const isAdded = pendingChangesActionType === PendingChangesActionType.ADDED;

      return (
        <Box sx={actionCellStyles}>
          <NavNeededCountBadge count={neededCountById[id] ?? 0} />
          <TeamChatReferenceBadge
            chatId={chatTargets?.internalChatId}
            entityId={id}
            entityType={ENTITY_REFERENCE_TYPE.ASSET}
          />
          <ActionButton
            chatTargets={chatTargets}
            entityType={ENTITY_REFERENCE_TYPE.ASSET}
            id={id}
            isHighlighted={isAdded}
            removeText="Delete Asset"
            tablePath={FINANCIAL_INFO_VIEWS_PATHS.ASSETS}
            onRemove={onRemove}
          />
        </Box>
      );
    },
    align: 'center',
  },
});
